import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { useEffect } from "react";
import type { LayoutDoc } from "../../shared/layout";
import type {
  AiStatus,
  CalendarChoice,
  CalendarEvent,
  CalendarFeed,
  CalendarStatus,
  DataState,
  Diagnostics,
  EventInput,
  HouseholdSettings,
  Me,
  LedgerEntry,
  Member,
  RadarTimeline,
  RewardBalance,
  RewardCurrency,
  Routine,
  RoutineDay,
  TickerItem,
  Task,
  TaskInput,
  WeatherSummary,
} from "../../shared/models";
import { api, ApiRequestError } from "./api";
import { readSnapshot, writeSnapshot } from "./persist";

const MIN = 60_000;

/**
 * useQuery plus a localStorage snapshot: the snapshot seeds the cache on
 * load with its real save time, and is refreshed after every successful
 * fetch. Until the server has answered once, the snapshot always counts as
 * stale, so every page load revalidates it. Query keys are shared, so any
 * number of widgets showing the same data share one request.
 */
function usePersistedQuery<T>(opts: { key: readonly unknown[]; fn: (signal: AbortSignal) => Promise<T>; refetchMs?: number; staleMs?: number; enabled?: boolean }) {
  const snapKey = JSON.stringify(opts.key);
  const staleMs = opts.staleMs ?? MIN;
  const q = useQuery({
    queryKey: opts.key,
    queryFn: ({ signal }) => opts.fn(signal),
    // Functions: storage is only read when the query is first created.
    initialData: () => readSnapshot<T>(snapKey)?.data,
    initialDataUpdatedAt: () => readSnapshot<T>(snapKey)?.savedAt,
    staleTime: (query) => (query.state.dataUpdateCount === 0 ? 0 : staleMs),
    refetchInterval: opts.refetchMs,
    enabled: opts.enabled ?? true,
  });
  useEffect(() => {
    if (q.data !== undefined && q.dataUpdatedAt && !q.isPlaceholderData && q.status === "success" && q.fetchStatus === "idle") {
      writeSnapshot(snapKey, q.data, q.dataUpdatedAt);
    }
  }, [q.data, q.dataUpdatedAt, q.isPlaceholderData, q.status, q.fetchStatus, snapKey]);
  return q;
}

/** Map a query to the widget data states from the product brief. */
export function dataStateOf(q: UseQueryResult<unknown>, staleAfterMs: number): DataState {
  const err = q.error instanceof ApiRequestError ? q.error : null;
  if (q.data === undefined) {
    if (q.isPending) return "loading";
    if (err?.status === 401 || err?.status === 403) return "permissionRequired";
    if (err?.code === "OFFLINE") return "offline";
    return "unavailable";
  }
  if (err?.code === "OFFLINE" || (typeof navigator !== "undefined" && !navigator.onLine)) return "offline";
  if (q.isError || Date.now() - q.dataUpdatedAt > staleAfterMs) return "stale";
  return "live";
}

// ---------- Session & household ----------

export function useAuthConfig() {
  return useQuery({ queryKey: ["auth-config"], queryFn: () => api<{ googleConfigured: boolean; devBypass: boolean; demoAvailable: boolean }>("/auth/config"), staleTime: Infinity });
}

export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: () => api<Me>("/me"),
    retry: (count, err) => !(err instanceof ApiRequestError && err.status === 401) && count < 3,
    staleTime: 5 * MIN,
  });
}

export function useSettings() {
  // A change made in Admin on a phone reaches the wall within a few minutes.
  return usePersistedQuery({ key: ["settings"], fn: (s) => api<HouseholdSettings>("/settings", { signal: s }), staleMs: 2 * MIN, refetchMs: 5 * MIN });
}

export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<HouseholdSettings>) => api<HouseholdSettings>("/settings", { method: "PATCH", body: patch }),
    onSuccess: (data) => {
      qc.setQueryData(["settings"], data);
      void qc.invalidateQueries({ queryKey: ["weather"] });
    },
  });
}

export function useMembers() {
  return usePersistedQuery({ key: ["members"], fn: (s) => api<Member[]>("/members", { signal: s }), staleMs: 5 * MIN, refetchMs: 10 * MIN });
}

// ---------- Layouts ----------

export function useLayouts() {
  return usePersistedQuery({ key: ["layouts"], fn: (s) => api<LayoutDoc[]>("/layouts", { signal: s }), staleMs: MIN, refetchMs: 5 * MIN });
}

// ---------- Weather ----------

export function useWeather(enabled: boolean) {
  return usePersistedQuery({
    key: ["weather"],
    fn: (s) => api<WeatherSummary>("/weather", { signal: s }),
    // Observations update roughly hourly; forecasts every few hours.
    refetchMs: 10 * MIN,
    staleMs: 5 * MIN,
    enabled,
  });
}

export function useRadarTimeline() {
  // MRMS makes a new radar frame every 2 minutes (the Worker caches the list for 2).
  return useQuery({ queryKey: ["radar-timeline"], queryFn: ({ signal }) => api<RadarTimeline>("/radar/timeline", { signal }), refetchInterval: 2 * MIN, staleTime: MIN });
}

// ---------- Calendar ----------

export function useCalendarEvents(start: string, end: string) {
  return usePersistedQuery({
    key: ["calendar", start, end],
    fn: (s) => api<CalendarFeed>(`/calendar/events?start=${start}&end=${end}`, { signal: s }),
    // The server syncs with Google at most about once a minute, so phone edits show up quickly.
    refetchMs: MIN,
    staleMs: 30_000,
  });
}

export function useCalendarStatus() {
  return useQuery({ queryKey: ["calendar-status"], queryFn: () => api<CalendarStatus>("/calendar/status"), staleTime: MIN });
}

/** The connected account's calendars, for the picker (adults only). */
export function useCalendarChoices(enabled: boolean) {
  return useQuery({ queryKey: ["calendar-choices"], queryFn: () => api<CalendarChoice[]>("/calendar/calendars"), enabled, staleTime: 5 * MIN });
}

export function useCalendarSetup() {
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["calendar"] });
    void qc.invalidateQueries({ queryKey: ["calendar-status"] });
    void qc.invalidateQueries({ queryKey: ["calendar-choices"] });
  };
  return {
    save: useMutation({
      mutationFn: (v: { selected: string[]; defaultId: string | null }) => api<{ selected: string[]; defaultId: string | null }>("/calendar/calendars", { method: "PUT", body: v }),
      onSuccess: refresh,
    }),
    disconnect: useMutation({
      mutationFn: () => api<{ ok: true }>("/calendar/disconnect", { method: "POST", body: {} }),
      onSuccess: () => {
        qc.removeQueries({ queryKey: ["calendar-choices"] });
        refresh();
      },
    }),
  };
}

const eventPath = (e: { calendarId: string; id: string }) => `/calendar/events/${encodeURIComponent(e.calendarId)}/${encodeURIComponent(e.id)}`;

/** Event changes are written to Google and only reported saved once Google confirms. */
export function useEventActions() {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ["calendar"] });
  return {
    create: useMutation({ mutationFn: (input: EventInput) => api<CalendarEvent>("/calendar/events", { method: "POST", body: input }), onSuccess: refresh }),
    update: useMutation({
      mutationFn: ({ event, input }: { event: CalendarEvent; input: EventInput }) => api<CalendarEvent>(eventPath(event), { method: "PATCH", body: input }),
      onSuccess: refresh,
    }),
    remove: useMutation({
      mutationFn: ({ event, scope }: { event: CalendarEvent; scope: "one" | "series" }) => api<{ ok: true }>(`${eventPath(event)}?scope=${scope}`, { method: "DELETE" }),
      onSuccess: refresh,
    }),
  };
}

/**
 * Move an event (drag to another day). Every cached calendar range shows it
 * moved straight away; a refusal from Google puts it back. Repeating events
 * move only this occurrence.
 */
export function useMoveEvent() {
  const qc = useQueryClient();
  const same = (a: CalendarEvent, b: CalendarEvent) => a.id === b.id && a.calendarId === b.calendarId;
  return useMutation({
    mutationFn: ({ event, input }: { event: CalendarEvent; moved: CalendarEvent; input: EventInput }) =>
      api<CalendarEvent>(eventPath(event), { method: "PATCH", body: { ...input, scope: "one" } }),
    onMutate: async ({ event, moved }) => {
      await qc.cancelQueries({ queryKey: ["calendar"] });
      const prev = qc.getQueriesData<CalendarFeed>({ queryKey: ["calendar"] });
      qc.setQueriesData<CalendarFeed>({ queryKey: ["calendar"] }, (f) => (f ? { ...f, events: f.events.map((e) => (same(e, event) ? moved : e)) } : f));
      return { prev };
    },
    onError: (_e, _v, ctx) => ctx?.prev.forEach(([key, data]) => qc.setQueryData(key, data)),
    onSuccess: (saved, { event }) => qc.setQueriesData<CalendarFeed>({ queryKey: ["calendar"] }, (f) => (f ? { ...f, events: f.events.map((e) => (same(e, event) ? saved : e)) } : f)),
    onSettled: () => qc.invalidateQueries({ queryKey: ["calendar"] }),
  });
}

// ---------- Tasks ----------

export function useTasks() {
  // Chores ticked off on a phone show on the wall within a couple of minutes.
  return usePersistedQuery({ key: ["tasks"], fn: (s) => api<Task[]>("/tasks", { signal: s }), refetchMs: 2 * MIN, staleMs: 30_000 });
}

/** Task mutations update the shared cache optimistically and roll back on failure. */
export function useTaskActions() {
  const qc = useQueryClient();
  const replace = (t: Task) => qc.setQueryData<Task[]>(["tasks"], (old) => (old ?? []).map((x) => (x.id === t.id ? t : x)));
  const refresh = () => qc.invalidateQueries({ queryKey: ["tasks"] });

  const optimistic = async (id: string, patch: Partial<Task>) => {
    await qc.cancelQueries({ queryKey: ["tasks"] });
    const prev = qc.getQueryData<Task[]>(["tasks"]);
    qc.setQueryData<Task[]>(["tasks"], (old) => (old ?? []).map((x) => (x.id === id ? { ...x, ...patch } : x)));
    return { prev };
  };
  const rollback = (_e: unknown, _v: unknown, ctx: { prev?: Task[] } | undefined) => {
    if (ctx?.prev) qc.setQueryData(["tasks"], ctx.prev);
  };

  return {
    create: useMutation({
      mutationFn: (input: TaskInput) => api<Task>("/tasks", { method: "POST", body: input }),
      onSuccess: (t) => qc.setQueryData<Task[]>(["tasks"], (old) => [...(old ?? []), t]),
      onSettled: refresh,
    }),
    update: useMutation({
      mutationFn: ({ id, patch }: { id: string; patch: TaskInput }) => api<Task>(`/tasks/${id}`, { method: "PATCH", body: patch }),
      onSuccess: replace,
      onSettled: refresh,
    }),
    complete: useMutation({
      mutationFn: ({ task, memberId }: { task: Task; memberId?: string }) => api<Task>(`/tasks/${task.id}/complete`, { method: "POST", body: memberId ? { memberId } : {} }),
      onMutate: ({ task }) => optimistic(task.id, task.recurrence ? { lastCompletedAt: new Date().toISOString() } : { completedAt: new Date().toISOString() }),
      onError: rollback,
      onSuccess: (t) => {
        replace(t);
        if (t.kind === "chore") void qc.invalidateQueries({ queryKey: ["rewards"] });
      },
      onSettled: refresh,
    }),
    reopen: useMutation({
      mutationFn: (task: Task) => api<Task>(`/tasks/${task.id}/reopen`, { method: "POST", body: {} }),
      onMutate: (task) => optimistic(task.id, { completedAt: null }),
      onError: rollback,
      onSuccess: (t) => {
        replace(t);
        if (t.kind === "chore") void qc.invalidateQueries({ queryKey: ["rewards"] });
      },
      onSettled: refresh,
    }),
    remove: useMutation({
      mutationFn: (task: Task) => api<{ ok: true }>(`/tasks/${task.id}`, { method: "DELETE" }),
      onSuccess: (_r, task) => qc.setQueryData<Task[]>(["tasks"], (old) => (old ?? []).filter((x) => x.id !== task.id)),
      onSettled: refresh,
    }),
  };
}

/** Tap-to-tag who an event involves; updates every cached calendar range. */
export function useSetEventPeople() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { calendarId: string; eventKey: string; memberIds: string[] }) =>
      api(`/calendar/events/${encodeURIComponent(v.calendarId)}/${encodeURIComponent(v.eventKey)}/people`, { method: "PUT", body: { memberIds: v.memberIds } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["calendar"] }),
  });
}

// ---------- Rewards & routines ----------

export function useRewards() {
  return usePersistedQuery({
    key: ["rewards"],
    fn: (s) => api<{ currency: RewardCurrency; balances: RewardBalance[] }>("/family/rewards", { signal: s }),
    refetchMs: 2 * MIN,
    staleMs: 30_000,
  });
}

export function useLedger(memberId: string | null) {
  return useQuery({
    queryKey: ["ledger", memberId],
    queryFn: () => api<LedgerEntry[]>(`/family/rewards/ledger${memberId ? `?memberId=${memberId}` : ""}`),
  });
}

export function useRoutines() {
  return useQuery({ queryKey: ["routines"], queryFn: () => api<Routine[]>("/family/routines"), staleTime: 5 * MIN });
}

export function useRoutineDay() {
  return usePersistedQuery({ key: ["routine-day"], fn: (s) => api<RoutineDay>("/family/routines/today", { signal: s }), refetchMs: 2 * MIN, staleMs: 30_000 });
}

export function useCheckRoutineStep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { stepId: string; memberId: string; checked: boolean }) =>
      api<{ ok: true; complete: boolean; rewarded: boolean }>("/family/routines/check", { method: "POST", body: v }),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: ["routine-day"] });
      const prev = qc.getQueryData<RoutineDay>(["routine-day"]);
      const key = `${v.stepId}:${v.memberId}`;
      qc.setQueryData<RoutineDay>(["routine-day"], (d) =>
        d ? { ...d, checked: v.checked ? [...new Set([...d.checked, key])] : d.checked.filter((k) => k !== key) } : d,
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => ctx?.prev && qc.setQueryData(["routine-day"], ctx.prev),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["routine-day"] });
      void qc.invalidateQueries({ queryKey: ["rewards"] });
    },
  });
}

// ---------- Ticker ----------

export function useTicker() {
  return usePersistedQuery({ key: ["ticker"], fn: (s) => api<{ date: string; items: TickerItem[] }>("/feeds/ticker", { signal: s }), refetchMs: 20 * MIN, staleMs: 10 * MIN });
}

// ---------- System ----------

export function useAiStatus() {
  return useQuery({ queryKey: ["ai-status"], queryFn: () => api<AiStatus>("/ai/status"), staleTime: 5 * MIN });
}

export function useDiagnostics(enabled: boolean) {
  return useQuery({ queryKey: ["diagnostics"], queryFn: () => api<Diagnostics>("/diagnostics"), enabled, refetchInterval: enabled ? MIN : false });
}
